import { DataWebRTC } from "../webrtc/data_webrtc";
import { ProgressWaiter } from "../common/timeout_waiter";
import { DownloaderWebrtcAction, } from "../common/downloader";
import { AppError } from "../common/app_error";
import { generateString } from "../common/util";
import { ControllerBase } from "./webrtc_controller";
import { PresetWebrtcAction, EventWebrtcAction, } from "../common/base";
export function setDeviceSnapshot(snapshot, imageImg, imageCanvas) {
    // not used for now
    if (snapshot.imageBlob) {
        imageImg.src = URL.createObjectURL(snapshot.imageBlob);
    }
    else {
        const properties = snapshot.properties;
        imageCanvas.width = properties.width;
        imageCanvas.height = properties.height;
        const context = imageCanvas.getContext("bitmaprenderer");
        context.transferFromImageBitmap(snapshot.imageBitmap);
    }
}
export class DataController extends ControllerBase {
    constructor(options) {
        super(options, DataWebRTC);
        this.waiter = new ProgressWaiter("DataController", DataController.SEND_TIMEOUT, options.debugging);
        this.webRTC.addEventListener(DataWebRTC.EVENT_DEVICE_SNAPSHOTS, (snapshot) => {
            if (this.debugging)
                console.debug("event_device_snapshots: snapshot=%s", JSON.stringify(snapshot));
            this.onEventSnapshot(snapshot);
        });
    }
    sendOffer(offer, traceHeader) {
        this.signal.sendDataOffer(this, {
            orgId: this.orgId,
            connectId: this.connectId,
            turn: this.turn,
            rtcToken: this.rtcToken,
            offer: offer.sdp,
            traceHeader
        });
    }
    doGetDesc() {
        return { connectId: this.connectId, nvrId: this.nvrId };
    }
    onEventSnapshot(snapshot) {
        const completing = (context) => {
            let done = false;
            let callbackObject = null;
            const camera = snapshot.camera;
            const key = camera.macAddress + camera.remoteIndex;
            if (context.reqCameras.has(key)) {
                context.resCameras.set(key, snapshot);
            }
            else {
                console.error("onEventSnapshot:: res camera not in reqCameras: key=%s", key);
            }
            // completed
            if (context.reqCameras.size == context.resCameras.size) {
                done = true;
                let error = null;
                const cameras = [];
                context.reqCameras.forEach((camera, key) => {
                    const cameraUnit = context.resCameras.get(key);
                    cameras.push(cameraUnit);
                    if (cameraUnit.error) {
                        error = cameraUnit.error;
                    }
                });
                callbackObject = { error, result: { cameras } };
            }
            return { done, callbackObject };
        };
        this.waiter.resetTimeout({ id: snapshot.actId, completing });
    }
    onDeviceTakeSnapshots(id, error, actRes) {
        const completing = (context) => {
            let result = null, done = false;
            let callbackObject = null;
            if (error) {
                // all failed
                done = true;
                result = { cameras: [] };
                context.reqCameras.forEach((camera) => {
                    result.cameras.push({ camera, error });
                });
                callbackObject = { error, result };
            }
            else {
                // some might fail
                const camerasRes = actRes.data.snapshotResult;
                camerasRes.forEach((item) => {
                    // care about errored camera only
                    if (item.error) {
                        const camera = item.camera;
                        const key = camera.macAddress + camera.remoteIndex;
                        if (context.reqCameras.has(key))
                            context.resCameras.set(key, { error: AppError.fromObject(item.error), camera });
                        else
                            console.error("onDeviceTakeSnapshots:: res camera not in reqCameras: key=%s", key);
                    }
                });
                if (context.reqCameras.size == context.resCameras.size) {
                    // all cameras failed
                    done = true;
                    const cameras = [];
                    context.reqCameras.forEach((camera, key) => {
                        cameras.push(context.resCameras.get(key));
                    });
                    result = { cameras };
                    callbackObject = { error: AppError.unknownError({ desc: "all cameras failed" }), result };
                }
            }
            return { done, callbackObject };
        };
        this.waiter.resetTimeout({ id, completing });
    }
    doDeviceTakeSnapshots(cameras, callback) {
        let e = null;
        if (this.webRTC.isConnected()) {
            const actId = generateString(31);
            const timeout = DataController.SEND_TIMEOUT + 1000 * cameras.length;
            this.stats.start({ key: DataController.STATS_DEVICE_SNAPSHOT_WS });
            e = this.signal.deviceTakeSnapshots({
                actId,
                orgId: this.orgId,
                nvrId: this.nvrId,
                connectId: this.connectId,
                cameras: cameras,
                timeout,
            }, (error, actRes) => {
                this.stats.end({ key: DataController.STATS_DEVICE_SNAPSHOT_WS, message: { error } });
                this.onDeviceTakeSnapshots(actId, error, actRes);
            });
            if (!e) {
                const timeoutCallback = (appError, context) => {
                    const cameras = [];
                    context.reqCameras.forEach((camera, key) => {
                        const resCamera = context.resCameras.get(key);
                        let cameraUnit;
                        if (resCamera === undefined) {
                            cameraUnit = { error: appError, camera };
                        }
                        else {
                            cameraUnit = resCamera;
                        }
                        cameras.push(cameraUnit);
                    });
                    if (callback) {
                        // completed
                        callback(appError, { cameras });
                    }
                };
                const context = {
                    reqCameras: new Map(),
                    resCameras: new Map(),
                };
                cameras.forEach((camera) => {
                    context.reqCameras.set(camera.macAddress + camera.remoteIndex, camera);
                });
                this.waiter.setTimeout({ id: actId, callback, context, timeoutCallback, timeout }, "doDeviceTakeSnapshots");
            }
        }
        else {
            e = AppError.webrtcDisconnectedError();
        }
        if (e && callback) {
            // all failed
            callback(e, {
                cameras: cameras.map((camera) => {
                    return { camera, error: e };
                }),
            });
        }
    }
    /*
     * There is no error when all succeed, but the error is set when at least one fails.
     * Each camera item (result.cameras[*]) in the callback contains error indicating whether it fails or not.
     * The result field in the callback cannot be null.
     * The result.cameras in the callback has the same order as the argument of cameras.
     */
    deviceTakeSnapshots(cameras, callback) {
        cameras.forEach((camera) => {
            this.stats.start({ key: DataController.STATS_DEVICE_SNAPSHOT + this.getCameraKey(camera) });
        });
        // result cannot be null
        this.doDeviceTakeSnapshots(cameras, (error, result) => {
            result?.cameras.forEach((cameraUnit) => {
                this.stats.end({
                    key: DataController.STATS_DEVICE_SNAPSHOT + this.getCameraKey(cameraUnit.camera),
                    message: { error: cameraUnit.error },
                });
            });
            if (callback)
                callback(error, result);
        });
    }
    //get dailyRecord for a month
    recordDaily(req, callback, timeout) {
        const stats = this.stats;
        const key = DownloaderWebrtcAction.RecordDaily;
        stats.start({ key: key, message: req });
        return this.webRTC.recordDaily(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const rsp = {
                camera: msg.data["camera"],
                streamId: msg.data["streamId"], // 0: HD stream,1:SD stream
                recordDailyList: msg.data["recordDailyList"]
            };
            callback?.(null, rsp);
        }, timeout);
    }
    //get file generation status :generating/generated/failed
    downloaderList(req, callback, timeout) {
        const stats = this.stats;
        const key = DownloaderWebrtcAction.List;
        stats.start({ key: key, message: req });
        return this.webRTC.downloaderList(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const rsp = {
                records: msg.data["records"],
                quota: msg.data["quota"]
            };
            callback?.(null, rsp);
        }, timeout);
    }
    videoDownload(req, callback, timeout) {
        if (req.speed > 1024) {
            return new Error("Download speed should less than 1024.");
        }
        req.content.range = "bytes=" + req.content.ranges.start + "-" + req.content.ranges.end;
        if (!timeout) {
            const chunkSize = req.content.ranges.end - req.content.ranges.start;
            timeout = Math.ceil(chunkSize / (req.speed * 1024)) * 1000;
            console.debug('videoDownload timeout', timeout);
        }
        console.debug('videoDownload range', req.content.range);
        const stats = this.stats;
        const key = DownloaderWebrtcAction.Download + ": " + req.camera.id + " " + req.content.range;
        stats.start({ key: key, message: req });
        return this.webRTC.downloadVideo(req, function (e, msg) {
            stats.end({ key: key, message: { error: e } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const rsp = {
                downloaderId: msg.data["downloaderId"],
                actorId: msg.data["actorId"],
                recordId: msg.data["recordId"],
                camera: msg.data["camera"],
                content: msg.data["content"],
                sentAt: msg.data["sentAt"],
                payload: msg.obj.binary,
            };
            callback?.(null, rsp);
        }, timeout);
    }
    videoAbort(req, callback) {
        const stats = this.stats;
        const key = DownloaderWebrtcAction.Abort + ": " + req.camera.id;
        stats.start({ key: key, message: req });
        return this.webRTC.abortVideo(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const rsp = {
                downloaderId: msg.data["downloaderId"],
                actorId: msg.data["actorId"],
                recordId: msg.data["recordId"],
                camera: msg.data["camera"],
            };
            callback?.(e, rsp);
        });
    }
    videoComplete(req, callback) {
        const stats = this.stats;
        const key = DownloaderWebrtcAction.Complete + ": " + req.camera.id;
        this.stats.start({ key: key, message: req });
        return this.webRTC.completeVideo(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const rsp = {
                downloaderId: msg.data["downloaderId"],
                actorId: msg.data["actorId"],
                recordId: msg.data["recordId"],
                camera: msg.data["camera"],
            };
            callback?.(e, rsp);
        });
    }
    getCameraKey(camera) {
        let key = "[";
        if (typeof camera.id === "number") {
            key += "id=" + camera.id + ", ";
        }
        key += "macAddress=" + camera.macAddress + ", ";
        key += "remoteIndex=" + camera.remoteIndex + "]";
        return key;
    }
    presetList(req, callback) {
        const stats = this.stats;
        const key = PresetWebrtcAction.LIST + ": " + req.camera.id;
        this.stats.start({ key: key, message: req });
        return this.webRTC.presetList(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const res = { presetList: [] };
            const presetList = [];
            msg.data?.presetList?.presetInfos?.forEach((preset) => {
                preset && presetList.push({ id: preset?.id, name: preset?.name, modifiedAt: preset?.modifiedAt });
            });
            res.presetList = presetList;
            callback?.(e, res);
        });
    }
    presetSet(req, callback) {
        const stats = this.stats;
        const key = PresetWebrtcAction.SET + ": " + req.camera.id;
        this.stats.start({ key: key, message: req });
        return this.webRTC.presetSet(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const res = {
                ok: msg.data?.ok,
            };
            callback?.(e, res);
        });
    }
    presetDelete(req, callback) {
        const stats = this.stats;
        const key = PresetWebrtcAction.DELETE + ": " + req.camera.id;
        this.stats.start({ key: key, message: req });
        return this.webRTC.presetDelete(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const res = {
                ok: msg.data?.ok,
            };
            callback?.(e, res);
        });
    }
    presetGoto(req, callback) {
        const stats = this.stats;
        const key = PresetWebrtcAction.GOTO + ": " + req.camera.id;
        this.stats.start({ key: key, message: req });
        return this.webRTC.presetGoto(req, function (e, msg) {
            stats.end({ key: key, message: { error: e, msg: msg } });
            if (e) {
                callback?.(e, null);
                return;
            }
            const res = {
                ok: msg.data?.ok,
            };
            callback?.(e, res);
        });
    }
    presetGetImg(req, callback, timeout) {
        const stats = this.stats;
        const key = PresetWebrtcAction.GET_IMG + ": " + req.camera.id;
        this.stats.start({ key: key, message: req });
        return this.webRTC.presetGetImg(req, (error, msg) => {
            stats.end({ key: key, message: { error: error, msg: msg } });
            callback?.(error, msg);
        }, timeout);
    }
    speakerPlay(param, callback) {
        return this.webRTC.speakerPlay(param, callback);
    }
    getMediaImg(req, callback) {
        const stats = this.stats;
        const key = EventWebrtcAction.GetMedia + ": " + req.eventId;
        this.stats.start({ key: key, message: req });
        return this.webRTC.getMediaImg(req, (error, msg) => {
            stats.end({ key: key, message: { error: error, msg: msg } });
            callback?.(error, msg);
        });
    }
}
DataController.SEND_TIMEOUT = 10000;
DataController.STATS_DEVICE_SNAPSHOT = "camera_snapshot";
DataController.STATS_DEVICE_SNAPSHOT_WS = "camera_snapshot_ws";
